import { api } from "boot/axios";
import useData from "src/use/data";
import useCore from "src/use/core";
import useTools from "src/use/tools";
import useMembership from "src/use/membership";
import moment from "moment";

const {
  buySellWeekly,
  buySellEarlyMonthly,
  buySellPreview,
  candleStickPattern,
  allSymbol,
  historyDataSearch,
} = useData();

const { allStock, allIndex, allSector, sectorDetail, brokerList, floatCap } =
  useCore();
const { asOf, getLatestFiscalYearQuarter } = useTools();
const { getMySignals } = useMembership();

export function loadSignals({ commit }) {
  return new Promise((resolve, reject) => {
    buySellWeekly()
      .then((res) => {
        return res.data;
      })
      .then((buySellRes) => {
        try {
          buySellEarlyMonthly().then(async (res) => {
            let as_of;
            await asOf().then((data) => {
              as_of = data.data;
            });
            const weeklyDate = await moment(as_of)
              .subtract(30, "d")
              .format("YYYY-MM-DD");

            if (res && res.data) {
              const overallSignal = [
                ...buySellRes,
                ...res.data.map((i) => {
                  i.earlyBuy_date = i.buy_date;
                  i.earlySell_date = i.sell_date;
                  delete i.buy_date;
                  delete i.sell_date;
                  return i;
                }),
              ];

              let tempSignal = await overallSignal.reduce((acc, curr) => {
                if (curr.buy_date) {
                  let buy = acc[curr.buy_date]?.buy ?? [];
                  acc = {
                    ...acc,
                    [curr.buy_date]: {
                      ...acc[curr.buy_date],
                      buy: [...buy, curr.symbol],
                    },
                  };
                }
                if (curr.sell_date) {
                  let sell = acc[curr.sell_date]?.sell ?? [];
                  acc = {
                    ...acc,
                    [curr.sell_date]: {
                      ...acc[curr.sell_date],
                      sell: [...sell, curr.symbol],
                    },
                  };
                }
                if (curr.earlyBuy_date) {
                  let buy = acc[curr.earlyBuy_date]?.earlyBuy ?? [];
                  acc = {
                    ...acc,
                    [curr.earlyBuy_date]: {
                      ...acc[curr.earlyBuy_date],
                      earlyBuy: [...buy, curr.symbol],
                    },
                  };
                }
                if (curr.earlySell_date) {
                  let sell = acc[curr.earlySell_date]?.earlySell ?? [];
                  acc = {
                    ...acc,
                    [curr.earlySell_date]: {
                      ...acc[curr.earlySell_date],
                      earlySell: [...sell, curr.symbol],
                    },
                  };
                }
                return acc;
              }, {});

              let filteredSignalObj = await Object.fromEntries(
                Object.entries(tempSignal).filter(([k1, v1]) => {
                  if (
                    moment(k1).isBetween(weeklyDate, as_of.as_of, "days", "[]")
                  ) {
                    return k1;
                  }
                })
              );
              commit("setSignals", filteredSignalObj);
              resolve(true);
            }
          });
        } catch (error) {
          reject(error);
        }
      })
      .catch((error) => {
        console.error({ error });
        reject(error);
      });
  });
}

export function loadSignalPreview({ commit }) {
  return new Promise((resolve, reject) => {
    buySellPreview()
      .then((res) => {
        commit("setSignalsPreview", res.data);
        resolve(true);
      })
      .catch((error) => {
        console.error({ error });
        reject(error);
      });
  });
}

export function loadCandleStickPatterns({ commit }) {
  return new Promise((resolve, reject) => {
    candleStickPattern(21)
      .then(async (res) => {
        let tempPatterns = await res.data.reduce((acc, curr) => {
          let currPattern = [];
          let bullisOrbearish = null;
          let currBullishOrBearish = {};
          switch (curr.value) {
            case 100:
              bullisOrbearish = "Bullish";
              break;
            case -100:
              bullisOrbearish = "Bearish";
              break;
          }
          if (acc[curr.date]) {
            if (acc[curr.date][bullisOrbearish]) {
              if (acc[curr.date][bullisOrbearish][curr.candle_type]) {
                currPattern = [
                  ...acc[curr.date][bullisOrbearish][curr.candle_type],
                ];
              }
            }
          }
          if (acc[curr.date]) {
            if (acc[curr.date][bullisOrbearish]) {
              currBullishOrBearish = {
                ...acc[curr.date][bullisOrbearish],
              };
            }
          }
          acc = {
            ...acc,
            [curr.date]: {
              ...acc[curr.date],
              date: curr.date,
              [bullisOrbearish]: {
                ...currBullishOrBearish,
                [curr.candle_type]: [...currPattern, curr.symbol],
              },
            },
          };
          return acc;
        }, {});

        commit("setCandleStickPatterns", tempPatterns);
        resolve(true);
      })
      .catch((error) => {
        console.error({ error });
        reject(error);
      });
  });
}

export function setBrokerData({ commit }) {
  return new Promise((resolve, reject) => {
    buySellPreview()
      .then((res) => {
        commit("setBrokerData", res.data);
        resolve(true);
      })
      .catch((error) => {
        console.error({ error });
        reject(error);
      });
  });
}

export function loadMySignals({ commit }) {
  return new Promise((resolve, reject) => {
    getMySignals()
      .then(async (res) => {
        let tempMySignal = await res.data.stocks.reduce((acc, curr) => {
          if (!acc[curr]) {
            acc[curr] = {
              stock: { name: "", symbol: curr },
              marketPrice: null,
              status: { sellSignal: null, buySignal: null },
              performance: { buyCount: 0, sellCount: 0 },
              profitLoss: {
                cumulativeProfit: 0,
                totalProfit: null,
                length: 0,
                daysDiffSum: 0,
              },
              brokerTopHold: [],
              brokerRelease: [],
              pricePerformance: { daily: {}, weekly: {}, monthly: {} },
              // signals: {
              //   buy: [], sell: [], earlyBuy: [], earlySell: []
              // }
              signals: {
                buySell: [],
                earlyBuySell: [],
              },
            };
          }
          return acc;
        }, {});
        commit("setMySignals", tempMySignal);
        resolve(true);
      })
      .catch((error) => {
        console.error({ error });
        reject(error);
      });
  });
}
export function getAllSymbols({ commit }) {
  return new Promise((resolve, reject) => {
    allSymbol()
      .then((res) => {
        commit("setAllSymbols", res.data);
        resolve(res.data);
      })
      .catch((error) => {
        // reject(error);
      });
  });
}

export function getAllStocks({ commit }) {
  return new Promise((resolve, reject) => {
    allStock()
      .then((res) => {
        commit("setAllStocks", res.data);
        resolve(res);
      })
      .catch((err) => {
        reject(err);
      });
  });
}
export function getAllIndexs({ commit }) {
  return new Promise((resolve, reject) => {
    allIndex()
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        reject(err);
      });
  });
}
export function getAllSectors({ commit }) {
  return new Promise((resolve, reject) => {
    allSector()
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        reject(err);
      });
  });
}

export function getAllSectorDetails({ commit }) {
  return new Promise((resolve, reject) => {
    sectorDetail()
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        reject(err);
      });
  });
}

export function getAllBrokerList({ commit }) {
  return new Promise((resolve, reject) => {
    brokerList()
      .then((res) => {
        commit("setAllBroker", res.data);
        resolve(res);
      })
      .catch((err) => {
        reject(err);
      });
  });
}
export function getHistoryDataSearch({ commit }, tvSymbol) {
  return new Promise((resolve, reject) => {
    historyDataSearch(tvSymbol).then((res) => {
      commit("setHistoryDataSearch", res.data);
      resolve();
    });
  });
}
export function latestFiscalYearQuarter({ commit }) {
  return new Promise((resolve, reject) => {
    getLatestFiscalYearQuarter().then((res) => {
      commit("setLatestFiscalYearQuarter", res.data);
      resolve(res.data);
    });
  });
}

export function getFloatCap({ commit }) {
  return new Promise((resolve, reject) => {
    floatCap().then((res) => {
      commit("setFloatCap", { data: res.data, type: "", res: res });
      resolve(res);
    });
  });
}
